import {
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { SalesVsExpensesPageQuery } from '../../../../../../services';
import React from 'react';
import { formatFloat } from '../../../../../../helpers';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';

export function UtilityTable({
                               salesVsExpensesQuery,
                               loading = false,
                           }: {
    salesVsExpensesQuery?: SalesVsExpensesPageQuery;
    loading?: boolean;
}) {
    const theme = useTheme();
    const alternateColor = theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0';

    // While the query is in flight, show a right-aligned skeleton bar in each
    // value cell instead of "0.00"; the static row labels stay put so the
    // summary keeps its shape.
    const val = (node: React.ReactNode) =>
        loading ? (
            <Skeleton
                variant="text"
                sx={{ display: 'inline-block', minWidth: 60 }}
            />
        ) : (
            node
        );
    const expensesOperationalSummary:
        | SalesVsExpensesPageQuery['getExpensesOperationalSummary']['expenses'][number]
        | undefined =
        salesVsExpensesQuery?.getExpensesOperationalSummary?.expenses?.[0];

    const salesOperationalSummary:
        | SalesVsExpensesPageQuery['getSalesOperationalSummary']['sales'][number]
        | undefined =
        salesVsExpensesQuery?.getSalesOperationalSummary?.sales?.[0];

    // Tax rows read a different basis than everything around them. A
    // reconciliation-only document is not spending and not revenue, so it stays
    // out of the totals, the unit prices and the kilos — but its IVA, IVA
    // retenido and ISR retenido are declared like any other document's, so they
    // must be counted. Only the tax cells below may read these two.
    const expensesTaxSummary:
        | SalesVsExpensesPageQuery['getExpensesTaxSummary']['expenses'][number]
        | undefined = salesVsExpensesQuery?.getExpensesTaxSummary?.expenses?.[0];

    const salesTaxSummary:
        | SalesVsExpensesPageQuery['getSalesTaxSummary']['sales'][number]
        | undefined = salesVsExpensesQuery?.getSalesTaxSummary?.sales?.[0];

    return (
        <>
            <Typography sx={{ mb: 2}}>
                Utilidad
            </Typography>
            <TableContainer>
                <Table size={'small'} padding={'none'}>
                    <TableHead><TableRow><TableCell sx={{ width: '30%'}}/><TableCell sx={{ width: '30%'}}/>
                        <TableCell sx={{ width: '30%'}}/>
                        <TableCell sx={{ width: '10%'}}/></TableRow></TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', px: 1}}>Utilidad</TableCell>
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    fontWeight: 'bold',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (salesOperationalSummary?.total_with_tax || 0) -
                                    (expensesOperationalSummary?.total_with_tax || 0),
                                ))}
                            </TableCell>
                        </TableRow>

                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold'}}>Gastos</TableCell>
                            <TableCell />
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold'
                                }}
                            >
                                {val(formatFloat(
                                    (expensesOperationalSummary?.total_with_tax || 0),
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>Precio (con IVA)</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    expensesOperationalSummary?.units_sold ? (expensesOperationalSummary?.total_with_tax || 0) / (expensesOperationalSummary?.units_sold || 0) : 0,
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>Precio (sin IVA)</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                   expensesOperationalSummary?.units_sold  ? (expensesOperationalSummary?.total || 0) / (expensesOperationalSummary?.units_sold || 0) : 0,
                                ))}
                            </TableCell>
                       </TableRow>
                        <TableRow >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>Kilos en materia prima</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (expensesOperationalSummary?.units_sold || 0),
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>IVA</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (expensesTaxSummary?.tax || 0),
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow
                        >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>IVA Retenido</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (expensesTaxSummary?.tax_retained || 0),
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>ISR Retenido</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (expensesTaxSummary?.non_tax_retained || 0),
                                ))}
                            </TableCell>
                        </TableRow>

                        {/* Ventas */}

                        <TableRow
                        >
                            <TableCell></TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold'}}>Ventas</TableCell>
                            <TableCell />
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold'
                                }}
                            >
                                {val(formatFloat(
                                    (salesOperationalSummary?.total_with_tax || 0),
                                ))}
                            </TableCell>
                        </TableRow>

                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left',}}>Kilos</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (salesOperationalSummary?.kilos_sold || 0),
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left',}}>Precio (con IVA)</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    salesOperationalSummary?.kilos_sold !== 0 ? ((salesOperationalSummary?.total_with_tax || 0)/ (salesOperationalSummary?.kilos_sold || 1)): 0,
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left',}}>Precio (sin IVA)</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    salesOperationalSummary?.kilos_sold !== 0 ? ((salesOperationalSummary?.total || 0)/ (salesOperationalSummary?.kilos_sold || 1)): 0,
                                ))}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left',}}>IVA</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (salesTaxSummary?.tax || 0),
                                ))}
                            </TableCell>
                        </TableRow>

                        {/* General — figures that belong to neither section
                            because they compare the two. This replaces the
                            `Diferencia` tile removed from Contabilidad; note it
                            is not the same number, because Contabilidad counts
                            only the documents its export includes while this
                            counts every document that carries IVA. */}

                        <TableRow
                            sx={{
                                backgroundColor:
                                    alternateColor,
                            }}
                        >
                            <TableCell></TableCell>
                            <TableCell sx={{ textAlign: 'left', fontWeight: 'bold'}}>General</TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell />
                            <TableCell sx={{ textAlign: 'left'}}>Diferencia IVA (Ventas - Gastos)</TableCell>
                            <TableCell
                                sx={{
                                    textAlign: 'right',
                                    px: 1,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {val(formatFloat(
                                    (salesTaxSummary?.tax || 0) -
                                    (expensesTaxSummary?.tax || 0),
                                ))}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
